// src/components/ui/Header.js - Supabase対応版（完全ログアウト実装）
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import InstructionNotificationBell from "../InstructionNotificationBell";
import ShiftChangeRequestAdminBell from "../ShiftChangeRequestAdminBell";
import DepartmentSwitcher from "../DepartmentSwitcher";
import {
  Home, FileEdit, BookOpen, ListChecks, CalendarRange, ClipboardList,
  Settings as SettingsIcon, BarChart3, TrendingUp, Upload, Database,
  LogOut, ChevronDown, ChevronUp,
} from "lucide-react";

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [staffName, setStaffName] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [userRole, setUserRole] = useState("");
  
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);
  
  // ログインページかどうかをチェック
  const isLoginPage = location.pathname === "/" || location.pathname === "/login";

  // 🔥 Internal モード判定 (= 自社スタッフが組織選択して動作中)。
  //   バナー (高さ 32px) が画面上部に重なっているため、 Header と body 全体を 32px 下げる。
  const isInternalMode = !!localStorage.getItem('internal_selected_org');
  const internalBannerOffset = isInternalMode ? 32 : 0;

  // ヘッダー表示時のbodyスタイル調整
  useEffect(() => {
    const originalBodyStyle = {
      paddingTop: document.body.style.paddingTop,
      marginLeft: document.body.style.marginLeft,
    };

    document.body.style.paddingTop = `${60 + internalBannerOffset}px`;
    document.body.style.marginLeft = (windowWidth >= 768 && location.pathname === "/home") ? '250px' : '0';
    document.body.style.transition = 'margin-left 0.3s ease, padding-top 0.3s ease';

    return () => {
      document.body.style.paddingTop = originalBodyStyle.paddingTop;
      document.body.style.marginLeft = originalBodyStyle.marginLeft;
    };
  }, [windowWidth, location.pathname, internalBannerOffset]);

  // Supabaseユーザー情報取得
  //   🔥 自社スタッフ (Internal) が組織を選択中なら admin ロールで表示する。
  //   これにより Header の管理者用メニュー (シフト作成 / 手術申込 / 設定 等) が全て見える。
  useEffect(() => {
    const internalOrgRaw = localStorage.getItem('internal_selected_org');
    if (internalOrgRaw) {
      try {
        const org = JSON.parse(internalOrgRaw);
        setStaffName(`🔧 Internal (${org?.name || org?.id || ''})`);
        setUserRole('admin');
        return;
      } catch (_) { /* fall through */ }
    }
    const supabaseUser = localStorage.getItem('supabase_user');
    if (supabaseUser) {
      const userData = JSON.parse(supabaseUser);
      setStaffName(userData.name || "ユーザー");
      setUserRole(userData.role || "staff");
    } else {
      setStaffName("");
      setUserRole("");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // ✅ 完全ログアウト処理
  const handleLogout = async () => {
    const confirmLogout = window.confirm("ログアウトしますか？");
    if (!confirmLogout) return;

    try {
      console.log("🚪 ログアウト処理開始...");
      
      // 1. Supabaseの正式なログアウト
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("❌ Supabaseログアウトエラー:", error);
      } else {
        console.log("✅ Supabaseログアウト成功");
      }

      // 2. LocalStorageのクリア
      //    🛡 セキュリティ: Internal モード関連と組織関連も明示的にクリア。
      //       残っていると次回別アカウントでログイン時に stale な組織 UI を描画してしまう。
      localStorage.removeItem('supabase_token');
      localStorage.removeItem('supabase_refresh_token');
      localStorage.removeItem('supabase_user');
      localStorage.removeItem('require_password_change');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('employeeId');
      localStorage.removeItem('currentOrganization');
      localStorage.removeItem('internal_selected_org');
      localStorage.removeItem('internal_edit_session');
      console.log("✅ LocalStorage クリア");
      
      // 3. SessionStorageもクリア
      sessionStorage.clear();
      console.log("✅ SessionStorage クリア");
      
      // 4. キャッシュをクリア
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
        console.log("✅ キャッシュ クリア");
      }

      // 5. ログイン画面へ強制リダイレクト
      console.log("🔄 ログイン画面へリダイレクト");
      window.location.href = "/";
      
    } catch (error) {
      console.error("💥 ログアウトエラー:", error);
      
      // エラーでも強制的にログアウト
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
    }
  };

  // ✅ 全メニュー項目を定義 (アイコン付き)
  // children を持つ項目はドロップダウングループ
  const allMenuItems = [
    // スタッフ共通メニュー
    { path: "/home", label: "ホーム", icon: Home, roles: ["admin", "staff"] },
    { path: "/form", label: "シフト提出", icon: FileEdit, roles: ["admin", "staff"] },
    { path: "/skills", label: "経験録", icon: BookOpen, roles: ["admin", "staff"] },

    // 管理者専用メニュー
    { path: "/checklist-manager", label: "チェックリスト管理", icon: ListChecks, roles: ["admin"] },
    { path: "/admin", label: "シフト作成", icon: CalendarRange, roles: ["admin"] },
    { path: "/surgery-request", label: "手術申込・配置作成", icon: ClipboardList, roles: ["admin"] },
    { path: "/settings", label: "設定", icon: SettingsIcon, roles: ["admin"] },
    {
      label: "教育状況分析",
      icon: BarChart3,
      roles: ["admin"],
      groupKey: "education",
      children: [
        { path: "/education-dashboard", label: "教育進捗ダッシュボード", icon: TrendingUp },
        { path: "/staff-assignment-import", label: "配置履歴インポート", icon: Upload },
        { path: "/staff-assignment-db", label: "過去配置データベース", icon: Database },
      ],
    },
  ];

  // ユーザーの権限に基づいてメニューをフィルタリング
  const menuItems = allMenuItems.filter(item => 
    item.roles.includes(userRole)
  );

  const isCurrent = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  // ログインページの場合はヘッダーを表示しない
  if (isLoginPage) {
    return null;
  }

  return (
    <>
      {/* サイドメニュー */}
      <div style={{
        width: "250px",
        position: "fixed",
        top: internalBannerOffset,
        left: 0,
        height: `calc(100vh - ${internalBannerOffset}px)`,
        backgroundColor: "white",
        boxShadow: "0 0 20px rgba(0, 0, 0, 0.05)",
        zIndex: 9000,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.3s ease",
        transform: (!menuOpen && location.pathname !== "/home") || (windowWidth < 768 && !menuOpen) 
          ? "translateX(-100%)" 
          : "translateX(0)",
      }}>
        {/* ロゴエリア */}
        <div style={{
          padding: "25px 20px 20px",
          borderBottom: "1px solid #f0f0f0",
        }}>
          <Link to="/home" style={{ textDecoration: "none" }}>
            <div style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: "700",
              fontSize: "1.8rem",
              background: "linear-gradient(120deg, #3F51B5, #2196F3)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.5px",
              marginBottom: "0.5rem",
            }}>
              ScrubEdge
            </div>
          </Link>
          <div style={{
            fontSize: "0.85rem",
            color: "#666",
          }}>
            手術室スタッフ支援システム
          </div>
          
          {/* ログインユーザー表示 */}
          {staffName && (
            <div style={{
              marginTop: "1rem",
              padding: "0.75rem",
              backgroundColor: "rgba(33, 150, 243, 0.1)",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}>
              <div style={{ color: "#2196F3", fontWeight: "600" }}>
                ログイン中
              </div>
              <div style={{ color: "#333", marginTop: "0.25rem" }}>
                {staffName}
              </div>
            </div>
          )}
        </div>

        {/* メニューリスト */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem 0.75rem",
        }}>
          {menuItems.map((item) => {
            if (item.children) {
              return (
                <GroupMenuItem
                  key={item.groupKey || item.label}
                  item={item}
                  isCurrent={isCurrent}
                  closeMenu={closeMenu}
                />
              );
            }
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "0.85rem 1rem",
                  textDecoration: "none",
                  borderRadius: "10px",
                  margin: "0.25rem 0",
                  color: isCurrent(item.path) ? "#2196F3" : "#333",
                  backgroundColor: isCurrent(item.path) ? "rgba(33, 150, 243, 0.1)" : "transparent",
                  transition: "all 0.2s ease",
                  fontWeight: isCurrent(item.path) ? "600" : "normal",
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent(item.path)) {
                    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.03)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent(item.path)) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {Icon && <Icon size={18} style={{ flexShrink: 0 }} />}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* ログアウトボタン */}
        <div style={{
          padding: "1rem",
          borderTop: "1px solid #f0f0f0",
        }}>
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              padding: "0.85rem",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#d32f2f";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(244, 67, 54, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f44336";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(244, 67, 54, 0.2)";
            }}
          >
            <LogOut size={16} style={{ marginRight: 6 }} />
            ログアウト
          </button>
        </div>
      </div>

      {/* シンプルヘッダー */}
      <header style={{
        position: "fixed",
        top: internalBannerOffset,
        left: (windowWidth >= 768 && location.pathname === "/home") ? "250px" : 0,
        right: 0,
        height: "60px",
        zIndex: 900,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backdropFilter: "blur(10px)",
      }}>
        {/* ハンバーガーメニューボタン - ホーム以外では常に表示 */}
        {(windowWidth < 768 || location.pathname !== "/home") && (
          <button 
            onClick={toggleMenu}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.5rem",
              padding: "5px 8px",
              borderRadius: "4px",
              color: "#333",
            }}
          >
            ☰
          </button>
        )}

        {/* PC表示時で、ホームの場合は空のdiv */}
        {windowWidth >= 768 && location.pathname === "/home" && <div></div>}

        {/* 右側：部署切替 + 通知ベル + ログアウトボタン */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {userRole === 'admin' && <DepartmentSwitcher />}
          <InstructionNotificationBell />
          {userRole === 'admin' && <ShiftChangeRequestAdminBell />}
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "none",
              border: "1px solid #f44336",
              color: "#f44336",
              fontSize: windowWidth < 768 ? "0.8rem" : "0.85rem",
              padding: windowWidth < 768 ? "8px 12px" : "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontWeight: "500",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f44336";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(244, 67, 54, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#f44336";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <LogOut size={14} />
            {windowWidth >= 768 && <span>ログアウト</span>}
          </button>
        </div>
      </header>

      {/* オーバーレイ */}
      {menuOpen && (
        <div
          onClick={closeMenu}
          style={{
            position: "fixed",
            top: internalBannerOffset,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 8999,
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// サイドメニューのドロップダウングループ (教育状況分析 等)
//   - デフォルトは閉じる (ユーザーが明示的に開く)
//   - 子のアクティブ状態による自動展開はしない
// =============================================================================
function GroupMenuItem({ item, isCurrent, closeMenu }) {
  const [open, setOpen] = useState(false);
  const anyChildCurrent = (item.children || []).some((c) => isCurrent(c.path));
  const Icon = item.icon;

  return (
    <div style={{ margin: "0.25rem 0" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "0.85rem 1rem",
          background: anyChildCurrent ? "rgba(33, 150, 243, 0.08)" : "transparent",
          border: "none",
          borderRadius: 10,
          color: anyChildCurrent ? "#2196F3" : "#333",
          fontWeight: anyChildCurrent ? 600 : "normal",
          cursor: "pointer",
          textAlign: "left",
          fontSize: "1rem",
          fontFamily: "inherit",
        }}
      >
        {Icon && <Icon size={18} style={{ flexShrink: 0 }} />}
        <span style={{ flex: 1 }}>{item.label}</span>
        {open ? <ChevronUp size={14} style={{ color: "#94a3b8" }} /> : <ChevronDown size={14} style={{ color: "#94a3b8" }} />}
      </button>
      {open && (
        <div style={{ paddingLeft: 12, borderLeft: "2px solid #e2e8f0", marginLeft: 12, marginTop: 4 }}>
          {item.children.map((child) => {
            const CIcon = child.icon;
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0.6rem 0.9rem",
                  textDecoration: "none",
                  borderRadius: 8,
                  margin: "0.2rem 0",
                  fontSize: "0.9rem",
                  color: isCurrent(child.path) ? "#2196F3" : "#555",
                  background: isCurrent(child.path) ? "rgba(33, 150, 243, 0.1)" : "transparent",
                  fontWeight: isCurrent(child.path) ? 600 : "normal",
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent(child.path)) {
                    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.03)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent(child.path)) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {CIcon && <CIcon size={14} style={{ flexShrink: 0 }} />}
                <span>{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Header;